import { RowNode } from 'ag-grid-community'
import { FlagxsColumnDef } from '../../../../containers/BulkSheetView/gridOptions/extension'
import { useRowNumber } from '../../../../page-properties/bulksheet-properties/rowNumber'
import { useCallback, useMemo } from 'react'
import { intl } from '../../../../../i18n'
import { TaskActualWorkProxyRegistrationRow } from '../../taskActualWorkProxyRegistration'
import {
  useAccountable,
  useActualEndDate,
  useActualStartDate,
  useAssignee,
  useCode,
  useDisplayName,
  useResponsible,
  useScheduledEndDate,
  useScheduledStartDate,
  useStatus,
  useTags,
  useTeam,
  useWatchers,
} from '../../../../page-properties/bulksheet-properties/wbsItem'
import {
  ClientSideTextFilter,
  CustomEnumFilter,
} from '../../../../containers/BulkSheetView/components/filter'
import { CustomEnumValue } from '../../../../../lib/commons/appFunction'
import { ColumnType } from '../../../../containers/commons/AgGrid'
import { CustomEnumCellEditor } from '../../../../containers/BulkSheetView/components/cellEditor'
import { CustomEnumCellRenderer } from '../../../../containers/BulkSheetView/components/cellRenderer'
import { getLabel } from '../../../../../lib/commons/i18nLabel'
import { WbsItemSelectColumnOptionType } from '../../../../hooks/useWbsItemSelectColumnOptions'

export const EXTERNAL_ID_CODE: string =
  'task.actual.work.proxy.registration.wbsItem.code'
export const EXTERNAL_ID_STATUS: string =
  'task.actual.work.proxy.registration.wbsItem.status'
export const EXTERNAL_ID_SUBSTATUS: string =
  'task.actual.work.proxy.registration.wbsItem.substatus'
export const EXTERNAL_ID_DISPLAY_NAME: string =
  'task.actual.work.proxy.registration.wbsItem.displayName'
export const EXTERNAL_ID_TEAM: string =
  'task.actual.work.proxy.registration.wbsItem.team'
export const EXTERNAL_ID_ACCOUNTABLE: string =
  'task.actual.work.proxy.registration.wbsItem.accountable'
export const EXTERNAL_ID_RESPONSIBLE: string =
  'task.actual.work.proxy.registration.wbsItem.responsible'
export const EXTERNAL_ID_ASSIGNEE: string =
  'task.actual.work.proxy.registration.wbsItem.assignee'
export const EXTERNAL_ID_WATCHERS: string =
  'task.actual.work.proxy.registration.wbsItem.watchers'
export const EXTERNAL_ID_PRIORITY: string = 'body.wbsItem.priority'

const FIELD_WBSITEM_SCHEDULED_START = 'body.wbsItem.scheduledDate.startDate'
const FIELD_WBSITEM_SCHEDULED_END = 'body.wbsItem.scheduledDate.endDate'
export const FIELD_WBSITEM_ACTUALDATE_START =
  'body.wbsItem.actualDate.startDate'
export const FIELD_WBSITEM_ACTUALDATE_END = 'body.wbsItem.actualDate.endDate'

export const EXTERNAL_ID_SCHEDULED_START_DATE: string =
  'task.actual.work.proxy.registration.wbsItem.scheduledDate.startDate'
export const EXTERNAL_ID_SCHEDULED_END_DATE: string =
  'task.actual.work.proxy.registration.wbsItem.scheduledDate.endDate'
export const EXTERNAL_ID_ACTUAL_START_DATE: string =
  'task.actual.work.proxy.registration.wbsItem.actualDate.startDate'
export const EXTERNAL_ID_ACTUAL_END_DATE: string =
  'task.actual.work.proxy.registration.wbsItem.actualDate.endDate'

export const EXTERNAL_ID_PATH: string = 'body.path'
export const EXTERNAL_ID_PARENT_WBSITEM: string =
  'body.parentWbsItem.displayName'

/**
 * Common column defs
 */
export const useDefaultColumnGroupDef = (): FlagxsColumnDef => {
  const rowNumberColDef = useRowNumber()
  const columnGroupDef = useMemo(
    () =>
      ({
        externalId: ' ',
        headerName: intl.formatMessage({ id: ' ' }),
        children: [
          {
            field: 'uuid',
            hide: true,
            suppressColumnsToolPanel: true,
          },
          {
            colId: 'blank',
            hide: true,
            width: 35,
            suppressColumnsToolPanel: true,
          },
          {
            ...rowNumberColDef,
            lockPosition: 'left',
            pinned: true,
            cellRendererParams: params => {
              const node: RowNode = params.node
              return { value: node.data.rowNumber || '' }
            },
          },
        ],
      }) as FlagxsColumnDef,
    [rowNumberColDef]
  )
  return columnGroupDef
}

const statusCombinedValuePath = (combinedEnumCode: string) => {
  switch (combinedEnumCode) {
    case 'WBS_TYPE':
      return 'wbsItem.type'
    case 'TICKET_TYPE':
      return 'wbsItem.ticketType'
    case 'WBS_STATUS':
      return 'wbsItem.status'
    case 'WBS_SUBSTATUS':
      return 'wbsItem.substatus'
  }
  return undefined
}

export const generateCustomEnumColumnDef = (
  externalId: string,
  field: string,
  headerNameId: string,
  customEnumCode: string,
  context?: {
    [key: string]: CustomEnumValue[]
  }
): FlagxsColumnDef => {
  return {
    externalId,
    field,
    headerName: intl.formatMessage({ id: headerNameId }),
    width: 90,
    type: [ColumnType.customEnum],
    cellEditor: CustomEnumCellEditor,
    cellEditorParams: { customEnumCode },
    cellRenderer: CustomEnumCellRenderer,
    cellRendererParams: { customEnumCode },
    filter: CustomEnumFilter,
    floatingFilter: true,
    filterParams: {
      customEnumCode,
      getValue: option => option.value,
      getLabel: option => getLabel(option.nameI18n),
      sortValues: (options, context) => {
        const customEnums = context[customEnumCode]
        if (!customEnums) return options
        return options.sort((a, b) => {
          return (
            customEnums.findIndex(e => e.value === a.value) -
            customEnums.findIndex(e => e.value === b.value)
          )
        })
      },
    },
    comparator: (a: string, b: string) => {
      if (!context) return 0
      const options = context[customEnumCode]
      if (!options) return 0
      return (
        options.findIndex(o => o.value === a) -
        options.findIndex(o => o.value === b)
      )
    },
  } as FlagxsColumnDef
}

export const useInformationColumnGroupDef = (
  projectUuid: string,
  context: WbsItemSelectColumnOptionType | undefined
): FlagxsColumnDef => {
  const codeCol = useCode('body.wbsItem.code')
  const statusCol = useStatus({
    field: 'body.wbsItem.status',
    subStatusField: 'wbsItem.substatus',
    statusCombinedValuePath,
  })
  const getWbsItemProjectUuid = useCallback(
    (row: TaskActualWorkProxyRegistrationRow | undefined) =>
      row?.body?.wbsItem?.projectUuid || undefined,
    []
  )
  const tagsCol = useTags({
    field: 'body.wbsItem.tags',
    getWbsItemProjectUuid,
  })

  const columnGroupDef = useMemo(
    () =>
      ({
        externalId: 'task.actual.work.proxy.registration.basic',
        headerName: intl.formatMessage({ id: 'projectPlan.information' }),
        children: [
          { ...codeCol, lockPosition: 'left' },
          {
            ...statusCol,
            editable: false,
            lockPosition: 'left',
            width: 100,
          },
          {
            ...tagsCol,
            editable: false,
          },
          {
            ...generateCustomEnumColumnDef(
              EXTERNAL_ID_PRIORITY,
              EXTERNAL_ID_PRIORITY,
              'wbsItem.property.priority',
              'priority',
              context as { [key: string]: CustomEnumValue[] }
            ),
            width: 65,
          },
        ],
      }) as FlagxsColumnDef,
    [codeCol, context, statusCol, tagsCol]
  )
  return columnGroupDef
}

export const useDisplayNameColumnDef = (): FlagxsColumnDef => {
  const displayNameCol = useDisplayName({
    field: 'body.wbsItem.displayName',
  })
  const columnDef = useMemo(
    () =>
      ({
        ...displayNameCol,
        editable: false,
      }) as FlagxsColumnDef,
    [displayNameCol]
  )
  return columnDef
}

export const useAssignmentColumnGroupDef = (selectColumnOptionMap: {
  [key: string]: any
}): FlagxsColumnDef => {
  const teamCol = useTeam({
    field: 'body.wbsItem.team',
    gridOptionContext: selectColumnOptionMap,
  })
  const accountableCol = useAccountable({
    field: 'body.wbsItem.accountable',
  })
  const responsibleCol = useResponsible({
    field: 'body.wbsItem.responsible',
  })
  const assigneeCol = useAssignee({
    field: 'body.wbsItem.assignee',
  })
  const watchersCol = useWatchers({
    field: 'body.wbsItem.watchers',
  })

  const columnGroupDef = useMemo(
    () =>
      ({
        externalId: 'task.actual.work.proxy.registration.assignment',
        headerName: intl.formatMessage({ id: 'projectPlan.assignment' }),
        children: [
          {
            externalId: EXTERNAL_ID_TEAM,
            ...teamCol,
            editable: false,
          },
          {
            externalId: EXTERNAL_ID_ACCOUNTABLE,
            ...accountableCol,
            editable: false,
            width: 65,
          },
          {
            externalId: EXTERNAL_ID_RESPONSIBLE,
            ...responsibleCol,
            editable: false,
            width: 65,
          },
          {
            externalId: EXTERNAL_ID_ASSIGNEE,
            ...assigneeCol,
            editable: false,
            width: 65,
          },
          {
            externalId: EXTERNAL_ID_WATCHERS,
            ...watchersCol,
            editable: false,
          },
        ],
      }) as FlagxsColumnDef,
    [teamCol, accountableCol, responsibleCol, assigneeCol, watchersCol]
  )
  return columnGroupDef
}

export const useTermColumnGroupDef = (): FlagxsColumnDef => {
  const getWbsItemStatus = useCallback(
    (row: TaskActualWorkProxyRegistrationRow | undefined) =>
      row?.body?.wbsItem?.status,
    []
  )
  const getScheduledDate = useCallback(
    (row: TaskActualWorkProxyRegistrationRow | undefined) =>
      row?.body?.wbsItem?.scheduledDate,
    []
  )
  const getActualDate = useCallback(
    (row: TaskActualWorkProxyRegistrationRow | undefined) =>
      row?.body?.wbsItem?.actualDate,
    []
  )

  const dummySetDate = useCallback(() => {
    // do nothing.
  }, [])

  const scheduledStartDateColumnDef = useScheduledStartDate({
    field: FIELD_WBSITEM_SCHEDULED_START,
    getScheduledDate,
    setScheduledEndDate: dummySetDate,
  })

  const scheduledEndDateColumnDef = useScheduledEndDate({
    field: FIELD_WBSITEM_SCHEDULED_END,
    getScheduledDate,
    setScheduledStartDate: dummySetDate,
  })

  const actualStartDateColumnDef = useActualStartDate({
    field: FIELD_WBSITEM_ACTUALDATE_START,
    getWbsItemStatus,
    getScheduledDate,
    getActualDate,
    setActualEndDate: dummySetDate,
  })

  const actualEndDateColumnDef = useActualEndDate({
    field: FIELD_WBSITEM_ACTUALDATE_END,
    getWbsItemStatus,
    getScheduledDate,
    getActualDate,
    setActualStartDate: dummySetDate,
  })

  const groupDef = useMemo(
    () => ({
      externalId: 'task.actual.work.proxy.registration.term',
      headerName: intl.formatMessage({ id: 'projectPlan.term' }),
      children: [
        {
          externalId: EXTERNAL_ID_SCHEDULED_START_DATE,
          ...scheduledStartDateColumnDef,
          editable: false,
        },
        {
          externalId: EXTERNAL_ID_SCHEDULED_END_DATE,
          ...scheduledEndDateColumnDef,
          editable: false,
        },
        {
          externalId: EXTERNAL_ID_ACTUAL_START_DATE,
          ...actualStartDateColumnDef,
          editable: false,
        },
        {
          externalId: EXTERNAL_ID_ACTUAL_END_DATE,
          ...actualEndDateColumnDef,
          editable: false,
        },
      ],
    }),
    [
      scheduledStartDateColumnDef,
      scheduledEndDateColumnDef,
      actualStartDateColumnDef,
      actualEndDateColumnDef,
    ]
  )

  return groupDef
}

export const PATH_COLUMN_GROUP_DEF: FlagxsColumnDef = {
  externalId: 'task.actual.work.proxy.registration.path',
  headerName: intl.formatMessage({ id: 'wbsItemSearch.path' }),
  children: [
    {
      externalId: EXTERNAL_ID_PATH,
      field: EXTERNAL_ID_PATH,
      headerName: intl.formatMessage({ id: 'wbsItemSearch.path' }),
      width: 120,
      pinned: true,
      lockPosition: 'left',
      filter: ClientSideTextFilter,
      floatingFilter: true,
      cellStyle: {
        direction: 'rtl',
      },
    },
    {
      externalId: EXTERNAL_ID_PARENT_WBSITEM,
      field: EXTERNAL_ID_PARENT_WBSITEM,
      headerName: intl.formatMessage({ id: 'wbsItemSearch.parentWbsItem' }),
      width: 100,
      pinned: true,
      lockPosition: 'left',
      filter: ClientSideTextFilter,
      floatingFilter: true,
    },
  ],
} as FlagxsColumnDef
